import React, { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X, MoreVertical } from "lucide-react";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

/**
 * Install-as-app button. Uses the native beforeinstallprompt on Android/Chrome/Edge,
 * shows "Add to Home Screen" steps on iOS Safari, and a generic browser-menu hint
 * elsewhere. Renders nothing once the app is already installed.
 */
export default function InstallPWA({ className = "", label = "Install app" }) {
  const [deferred, setDeferred] = useState(null);
  const [helpMode, setHelpMode] = useState(null); // "ios" | "generic" | null
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch {
        /* dismissed */
      }
      setDeferred(null);
    } else if (isIOS()) {
      setHelpMode("ios");
    } else {
      setHelpMode("generic");
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="install-pwa-btn"
        onClick={onClick}
        className={
          className ||
          "inline-flex items-center gap-2 rounded-full bg-[#6F8F72] text-white font-extrabold px-5 py-2.5 hover:bg-[#61805f] transition-colors"
        }
      >
        <Download size={17} /> {label}
      </button>

      {helpMode && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4"
          onClick={() => setHelpMode(null)}
          data-testid="install-pwa-help"
        >
          <div
            className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-xl font-extrabold">Install Wooflect</h3>
              <button
                onClick={() => setHelpMode(null)}
                data-testid="install-pwa-help-close"
                className="h-9 w-9 rounded-full bg-[#F2EFE9] flex items-center justify-center"
              >
                <X size={18} />
              </button>
            </div>

            {helpMode === "ios" ? (
              <div className="space-y-3 text-[#2D2B2A]">
                <p className="text-sm text-[#4A4746]">
                  Add Wooflect to your Home Screen to use it like a native app —
                  no App Store needed.
                </p>
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-xl bg-[#F6DED5] flex items-center justify-center shrink-0">
                    <Share size={17} color="#E86A45" />
                  </div>
                  <p className="text-sm">
                    <b>1.</b> Tap the <b>Share</b> icon in Safari&apos;s toolbar.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-xl bg-[#F6DED5] flex items-center justify-center shrink-0">
                    <PlusSquare size={17} color="#E86A45" />
                  </div>
                  <p className="text-sm">
                    <b>2.</b> Choose <b>Add to Home Screen</b>, then tap <b>Add</b>.
                  </p>
                </div>
                <p className="text-xs text-[#9A938B] pt-1">
                  On iPhone/iPad this must be done in <b>Safari</b>.
                </p>
              </div>
            ) : (
              <div className="space-y-3 text-[#2D2B2A]">
                <p className="text-sm text-[#4A4746]">
                  Install Wooflect from your browser to launch it from your home
                  screen or desktop.
                </p>
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-xl bg-[#F6DED5] flex items-center justify-center shrink-0">
                    <MoreVertical size={17} color="#E86A45" />
                  </div>
                  <p className="text-sm">
                    Open your browser <b>menu</b> and choose{" "}
                    <b>Install app</b> or <b>Add to Home screen</b>.
                  </p>
                </div>
                <p className="text-xs text-[#9A938B] pt-1">
                  Works best in Chrome, Edge, or Safari on your phone.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
